import { formatCurrency } from './formatters';

export type GridValueFormat = 'text' | 'date' | 'currency' | 'number';

export type AddableGridColumn = {
  id: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  format: GridValueFormat;
};

/** Mismo límite que el contrato de preferencias: ids de 1 a 64, `[a-zA-Z0-9_]`. */
const ID_COLUMNA_RE = /^[a-zA-Z0-9_]{1,64}$/;
const CLAVES_INTERNAS = new Set(['detalles', 'fotos']);

const ETIQUETAS: Record<string, string> = {
  nro: 'Nro.',
  numero: 'Número',
  cod: 'Cód.',
  codigo: 'Código',
  desc: 'Desc.',
  descripcion: 'Descripción',
  fecha: 'Fecha',
  hora: 'Hora',
  vencim: 'Vencimiento',
  vencimiento: 'Vencimiento',
  vto: 'Vto.',
  cae: 'CAE',
  cuit: 'CUIT',
  iva: 'IVA',
  id: 'Id',
  min: 'mín.',
  max: 'máx.',
  porc: '%',
  sin: 'sin',
  de: 'de',
  del: 'del',
};

const ETIQUETAS_CAMPOS: Record<string, string> = {
  direccioncliente: 'Direccion Cliente',
  direccionenvio: 'Direccion Envio',
};

const esValorEscalar = (value: unknown): boolean => {
  if (value == null) return true;
  const tipo = typeof value;
  return tipo === 'string' || tipo === 'number' || tipo === 'boolean' || value instanceof Date;
};

export function esIdColumnaAgregable(id: string): boolean {
  if (!ID_COLUMNA_RE.test(id)) return false;
  if (CLAVES_INTERNAS.has(id.toLowerCase())) return false;
  if (/navigation$/i.test(id)) return false;
  return true;
}

export function etiquetarCampo(id: string): string {
  const etiquetaCampo = ETIQUETAS_CAMPOS[id.toLowerCase()];
  if (etiquetaCampo) return etiquetaCampo;

  const partes = id
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

  return partes
    .map((parte, index) => {
      const conocida = ETIQUETAS[parte.toLowerCase()];
      if (conocida) return conocida;
      const texto = parte.toLowerCase();
      return index === 0 ? texto.charAt(0).toUpperCase() + texto.slice(1) : texto;
    })
    .join(' ');
}

const pareceFecha = (value: unknown): boolean => {
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) return false;
  return !Number.isNaN(new Date(value).getTime());
};

const pareceNumero = (value: unknown): boolean => {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'string' || value.trim() === '') return false;
  return Number.isFinite(Number(value));
};

export function inferirFormato(id: string, samples: readonly unknown[]): GridValueFormat {
  const valores = samples.filter((value) => value != null && value !== '');
  if (/fecha|hora|vencim|vto/i.test(id)) return 'date';
  if (/codigo|^cod|barra|cuit|cae|letra|usuario|nombre|descripcion/i.test(id)) return 'text';
  if (valores.some(pareceFecha)) return 'date';

  const numericos = valores.length > 0 && valores.every(pareceNumero);
  if (!numericos && valores.length > 0) return 'text';
  if (/importe|precio|costo|monto|subtotal|total/i.test(id) && !/^nro/i.test(id)) return 'currency';
  if (numericos || /nro|cantidad|porc|stock|dias|cobertura|demanda|lead/i.test(id)) return 'number';
  return 'text';
}

export function alinearCampo(format: GridValueFormat): 'left' | 'right' {
  return format === 'currency' || format === 'number' ? 'right' : 'left';
}

export function formatGridFieldValue(value: unknown, format: GridValueFormat): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'object' && !(value instanceof Date)) return '—';

  if (format === 'currency') {
    const amount = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(amount) ? formatCurrency(amount) : '—';
  }
  if (format === 'number') {
    const amount = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(amount) ? amount.toLocaleString('es-AR') : '—';
  }
  if (format === 'date') {
    const date = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(date.getTime())) return '—';
    const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;
    return hasTime ? date.toLocaleString('es-AR') : date.toLocaleDateString('es-AR');
  }
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  return String(value);
}

/**
 * Campos escalares del ítem ya cargado que todavía no están en la grilla.
 * No hay catálogo: si el GET agrega una propiedad, aparece acá.
 */
export function descubrirColumnasDeItems(
  items: readonly object[],
  columnOrder: readonly string[],
): AddableGridColumn[] {
  const usadas = new Set(columnOrder);
  const muestras = new Map<string, unknown[]>();
  const rechazadas = new Set<string>();

  items.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    Object.keys(item).forEach((key) => {
      if (usadas.has(key) || !esIdColumnaAgregable(key) || rechazadas.has(key)) return;
      const value = (item as Record<string, unknown>)[key];
      if (!esValorEscalar(value)) {
        rechazadas.add(key);
        muestras.delete(key);
        return;
      }
      const actuales = muestras.get(key) ?? [];
      actuales.push(value);
      muestras.set(key, actuales);
    });
  });

  return Array.from(muestras.entries())
    .filter(([, samples]) => samples.some((value) => value != null && value !== ''))
    .map(([id, samples]) => {
      const format = inferirFormato(id, samples);
      return {
        id,
        label: etiquetarCampo(id),
        align: alinearCampo(format),
        format,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

export function presentarCampoGrilla(
  item: object,
  columnId: string,
): { texto: string; align: 'left' | 'right' } {
  const value = (item as Record<string, unknown>)[columnId];
  const format = inferirFormato(columnId, [value]);
  return {
    texto: formatGridFieldValue(value, format),
    align: alinearCampo(format),
  };
}
